import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { SelectedStylepackContext } from '../stylepack-context';
import styles from '../stylepacks.module.css';
import { formatMsToHMMSS } from '@/utils/dates';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import type { GraphicItem } from './video-list-graphic.types';
import { useHandleGoToGreenroom } from '@ui/greenroom-button/use-handle-go-to-greenroom';
import qs from 'qs';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { plural } from '@/utils/plural';

export default function VideoListItem(video: GraphicItem) {
  const { stylepack } = useContext(SelectedStylepackContext);
  const goToGreenroom = useHandleGoToGreenroom(
    qs.stringify({ [video.entityType]: video.id, shots: video.shots.map((shot) => shot.shot_id) })
  );

  const shotsList = useMemo(() => {
    if (video.shots.length) {
      return `${plural(video.shots.length, 'Shot')} ${video.shots.map((shot) => shot.shot_data.order + 1).join(', ')}`;
    }

    return '';
  }, [video]);

  return (
    <Container
      width="100%"
      padding="8px 16px 8px 8px"
      borderRadius="8px"
      className={`cursor-pointer ${styles.listItem}`}
      background="rgba(255, 255, 255, 0.05)"
      hoverBackground="rgba(255, 255, 255, 0.10)"
    >
      <Container className="flex flex-column jc-center">
        <img src={video.thumbnailUrl} className="radius-8 width-72 height-40" />
      </Container>
      <Container className="flex flex-column jc-center">
        <Text className="S2" design={video.name !== 'Untitled' ? 'GlobalIcoText1' : 'GlobalIcoText2'}>
          {video.shotTitle ?? stylepack.name}
        </Text>
        <Text design="GlobalIcoText4" className="L2" transform="capitalize">
          {video.contentType}
        </Text>
      </Container>
      <Container className="flex ai-center">
        <Text className="S2" design={'GlobalIcoText3'}>
          {shotsList}
        </Text>
      </Container>
      <Container className="flex ai-center">
        <Text design="GlobalIcoText3" className="L2">
          {formatMsToHMMSS((video.duration ?? 0) * 1000)}
        </Text>
      </Container>
      <Container gap="8px" className={`flex ai-center jc-flex-end ${styles.listItemActions}`}>
        <Link to={`./preview/greenroom`} relative="path" state={{ videoId: video.id }}>
          <Button designSize="small" backgroundColor={getThemeColorCSSVariable()}>
            <Text
              whiteSpace="nowrap"
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            >
              Preview
            </Text>
          </Button>
        </Link>
        <Button design="LocalBttnSecHalfTransp" designSize="small" round onClick={goToGreenroom}>
          <Icon name={ICONS.EDIT} size={12} />
        </Button>
      </Container>
    </Container>
  );
}
